import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import Card from '../common/Card';
import Button from '../common/Button';
import Input from '../common/Input';
import Modal from '../common/Modal';
import api from '../../services/api';
import { formatCurrency } from '../../utils/formatters';
import '../../styles/admin.css';

const EMPTY_MODULE = {
  module_code: '',
  module_name: '',
  description: '',
  icon: '📦',
  price_monthly: '',
  price_yearly: '',
  sort_order: 0,
  is_active: true
};

const ModuleManager = () => {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_MODULE);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadModules();
  }, []);

  const loadModules = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/modules');
      setModules(res.data.modules || []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load modules');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_MODULE);
    setShowModal(true);
  };

  const openEdit = (mod) => {
    setEditing(mod);
    setForm({
      module_code: mod.module_code,
      module_name: mod.module_name,
      description: mod.description || '',
      icon: mod.icon || '📦',
      price_monthly: mod.price_monthly,
      price_yearly: mod.price_yearly || '',
      sort_order: mod.sort_order || 0,
      is_active: !!Number(mod.is_active)
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.module_code || !form.module_name || form.price_monthly === '') {
      toast.error('Code, name, and monthly price are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        price_monthly: parseFloat(form.price_monthly),
        price_yearly: form.price_yearly === '' ? null : parseFloat(form.price_yearly),
        sort_order: parseInt(form.sort_order, 10) || 0
      };
      if (editing) {
        await api.put(`/admin/modules/${editing.id}`, payload);
        toast.success('Module updated');
      } else {
        await api.post('/admin/modules', payload);
        toast.success('Module created');
      }
      setShowModal(false);
      loadModules();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (mod) => {
    if (!window.confirm(`Remove or deactivate "${mod.module_name}"?`)) return;
    try {
      await api.delete(`/admin/modules/${mod.id}`);
      toast.success('Module removed');
      loadModules();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
    }
  };

  return (
    <div className="admin-page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <h1>App Modules</h1>
          <p className="page-subtitle">
            Manage POS, Inventory, Finance, CRM and set each app&apos;s monthly price. Tenants pick apps and pay the sum.
          </p>
        </div>
        <Button variant="primary" onClick={openCreate}>
          Add Module
        </Button>
      </div>

      {loading ? (
        <Card><p>Loading modules…</p></Card>
      ) : (
        <div className="modules-admin-grid">
          {modules.map((mod) => (
            <Card key={mod.id} className={!Number(mod.is_active) ? 'module-inactive' : ''}>
              <div className="module-admin-card">
                <div className="module-admin-icon">{mod.icon}</div>
                <div className="module-admin-body">
                  <div className="module-admin-title-row">
                    <h3>{mod.module_name}</h3>
                    <span className={`status-badge ${Number(mod.is_active) ? 'active' : 'inactive'}`}>
                      {Number(mod.is_active) ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="module-code">{mod.module_code}</p>
                  <p>{mod.description}</p>
                  <div className="module-price-row">
                    <strong>{formatCurrency(mod.price_monthly)}</strong>
                    <span>/month</span>
                    {mod.price_yearly != null && mod.price_yearly !== '' && (
                      <span className="yearly">· {formatCurrency(mod.price_yearly)}/year</span>
                    )}
                  </div>
                  <div className="action-buttons">
                    <Button size="sm" variant="secondary" onClick={() => openEdit(mod)}>
                      Edit
                    </Button>
                    <Button size="sm" variant="danger" onClick={() => handleDelete(mod)}>
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editing ? 'Edit Module' : 'Add Module'}
      >
        <div className="form-grid">
          <Input
            label="Code"
            name="module_code"
            value={form.module_code}
            placeholder="pos, inventory, finance, crm"
            onChange={(e) => setForm({ ...form, module_code: e.target.value })}
            required
          />
          <Input
            label="Name"
            name="module_name"
            value={form.module_name}
            onChange={(e) => setForm({ ...form, module_name: e.target.value })}
            required
          />
          <Input
            label="Icon (emoji)"
            name="icon"
            value={form.icon}
            onChange={(e) => setForm({ ...form, icon: e.target.value })}
          />
          <Input
            label="Sort order"
            name="sort_order"
            type="number"
            value={form.sort_order}
            onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
          />
          <Input
            label="Monthly price (KES)"
            name="price_monthly"
            type="number"
            value={form.price_monthly}
            onChange={(e) => setForm({ ...form, price_monthly: e.target.value })}
            required
          />
          <Input
            label="Yearly price (KES)"
            name="price_yearly"
            type="number"
            value={form.price_yearly}
            onChange={(e) => setForm({ ...form, price_yearly: e.target.value })}
          />
        </div>
        <Input
          type="textarea"
          label="Description"
          name="description"
          rows={3}
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={!!form.is_active}
            onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
          />
          Active (visible to tenants)
        </label>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1rem' }}>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" loading={saving} onClick={handleSave}>
            Save
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default ModuleManager;
